import { useState } from "react"
import { Modal } from "common/Modal"
import { formatDate } from "utils/funcs"
import { getOperationExpenses, getExpensesTotal, isImageFile } from "./utils"
import { EditExpenseModal } from "./EditExpenseModal"
import { ImagePreviewModal } from "./ImagePreviewModal"

type Props = {
    showModal: boolean
    setShowModal: (value: boolean) => void
    operation: any
    onAddExpense: () => void
    onDeleteExpense: (expenseId: string, operationId: string) => void
    onExpenseUpdated: () => void
}

const IconView = () => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
)

const IconDownload = () => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
    </svg>
)

const IconEdit = () => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
        <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10" />
    </svg>
)

const IconDelete = () => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
    </svg>
)

export const ExpensesModal = ({
    showModal,
    setShowModal,
    operation,
    onAddExpense,
    onDeleteExpense,
    onExpenseUpdated,
}: Props) => {
    const [editingExpense, setEditingExpense] = useState<any>(null)
    const [showEditExpenseModal, setShowEditExpenseModal] = useState(false)
    const [previewFile, setPreviewFile] = useState<any>(null)

    const handleFileClick = (file: any) => {
        if (isImageFile(file.type)) {
            setPreviewFile(file)
        } else {
            window.open(file.url, "_blank")
        }
    }

    const handleEditExpense = (expense: any) => {
        setEditingExpense(expense)
        setShowEditExpenseModal(true)
    }

    const handleEditSuccess = () => {
        setEditingExpense(null)
        setShowEditExpenseModal(false)
        onExpenseUpdated()
    }

    if (!operation) {
        return (
            <Modal
                showModal={showModal}
                setShowModal={setShowModal}
                title="Expenses Details"
                width="w-full md:w-2/4"
            >
                <p className="text-gray-500 text-center py-8">No operation selected</p>
            </Modal>
        )
    }

    const expenses = getOperationExpenses(operation)
    const totalAmount = getExpensesTotal(expenses)

    const handleAddClick = () => {
        setShowModal(false)
        onAddExpense()
    }

    if (expenses.length === 0) {
        return (
            <>
                <Modal
                    showModal={showModal}
                    setShowModal={setShowModal}
                    title="Expenses Details"
                    width="w-full md:w-2/4"
                >
                    <div className="text-center py-8">
                        <p className="text-gray-500 mb-4">No expenses found</p>
                        <button
                            onClick={handleAddClick}
                            className="px-4 py-2 bg-green-500 text-white text-sm font-medium rounded-md hover:bg-green-600 transition-colors"
                        >
                            Add Expense
                        </button>
                    </div>
                </Modal>
            </>
        )
    }

    return (
        <>
            <Modal
                showModal={showModal}
                setShowModal={setShowModal}
                title="Expenses Details"
                width="w-full md:w-2/4"
            >
                <div className="space-y-4 max-h-[70vh] overflow-y-auto">
                    <div className="mb-4 p-3 bg-gray-50 rounded-lg flex justify-between items-center">
                        <div>
                            <p className="text-sm text-gray-600">
                                <span className="font-medium">Total Expenses:</span>{" "}
                                <span className="text-green-600 font-semibold">${totalAmount.toFixed(2)}</span>
                            </p>
                            <p className="text-sm text-gray-600 mt-1">
                                <span className="font-medium">Number of Expenses:</span> {expenses.length}
                            </p>
                        </div>
                        <button
                            onClick={handleAddClick}
                            className="px-3 py-1.5 bg-green-500 text-white text-sm font-medium rounded-md hover:bg-green-600 transition-colors"
                        >
                            Add Expense
                        </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {expenses.map((expense: any, index: number) => (
                            <div
                                key={expense._id || index}
                                className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow relative"
                            >
                                <div className="absolute top-2 right-2 z-10 flex gap-1">
                                    <button
                                        onClick={() => handleEditExpense(expense)}
                                        className="text-blue-500 hover:text-blue-700 p-1.5 rounded-md hover:bg-blue-50 transition-colors bg-white shadow-sm"
                                        title="Edit expense"
                                        type="button"
                                    >
                                        <IconEdit />
                                    </button>
                                    <button
                                        onClick={() => onDeleteExpense(expense._id, operation._id)}
                                        className="text-red-500 hover:text-red-700 p-1.5 rounded-md hover:bg-red-50 transition-colors bg-white shadow-sm"
                                        title="Delete expense"
                                        type="button"
                                    >
                                        <IconDelete />
                                    </button>
                                </div>
                                <div className="flex justify-between items-start mb-3 pr-20">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-4 text-sm text-gray-700 flex-wrap">
                                            <span>
                                                <span className="font-medium">Amount:</span>{" "}
                                                <span className="text-green-600 font-semibold">
                                                    ${parseFloat(expense.amount || 0).toFixed(2)}
                                                </span>
                                            </span>
                                            {expense.date && (
                                                <span>
                                                    <span className="font-medium">Date:</span> {formatDate(expense.date)}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {expense.files && expense.files.length > 0 && (
                                    <div className="mt-4 pt-4 border-t border-gray-200">
                                        <label className="text-sm font-medium text-gray-500 mb-2 block">
                                            Attachments ({expense.files.length})
                                        </label>
                                        <div className="flex flex-wrap gap-3">
                                            {expense.files.map((file: any) => (
                                                <div
                                                    key={file._id}
                                                    className="flex items-center gap-2 p-2 bg-gray-50 rounded border border-gray-200 hover:bg-gray-100 transition"
                                                >
                                                    {isImageFile(file.type) ? (
                                                        <button
                                                            type="button"
                                                            onClick={() => handleFileClick(file)}
                                                            className="flex items-center gap-2 text-blue-600 hover:text-blue-800"
                                                        >
                                                            <IconView />
                                                            <span className="text-sm">{file.fileName}</span>
                                                        </button>
                                                    ) : (
                                                        <a
                                                            href={file.url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="flex items-center gap-2 text-blue-600 hover:text-blue-800"
                                                        >
                                                            <IconDownload />
                                                            <span className="text-sm">{file.fileName}</span>
                                                        </a>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </Modal>

            <EditExpenseModal
                showModal={showEditExpenseModal}
                setShowModal={setShowEditExpenseModal}
                operationId={operation._id}
                expense={editingExpense}
                onSuccess={handleEditSuccess}
            />

            <ImagePreviewModal
                showModal={!!previewFile}
                setShowModal={(open) => !open && setPreviewFile(null)}
                imageUrl={previewFile?.url || ""}
                file={previewFile}
            />
        </>
    )
}
